import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

// Keeps your imports
import worldMapImage from "../assets/world-optimized.webp";
import ammentun from "../assets/ammentun.png";
import dynCorp from "../assets/DynCorp_International_logo.png";
import ivyJapan from "../assets/IVY_Japan.png";
import kbr from "../assets/KBR.jpeg";
import mag from "../assets/MAG.png";
import samaritansPurse from "../assets/samaritan's purse.png";
import unfpa from "../assets/UNFPA_logo.png";
import unhcr from "../assets/unhcr.png";
import usArmy from "../assets/United_States_Army-optimized.webp";
import vectrus from "../assets/Vectrus.png";
import yazda from "../assets/Yazda_Global_Logo_menu_v3.png";

// --- DATA & CONFIG ---
const getPartners = (t) => [
  {
    name: "Ammentun",
    logo: ammentun,
    role: t("clients.partners.ammentun.role"),
    description: t("clients.partners.ammentun.description"),
  },
  {
    name: "DynCorp International",
    logo: dynCorp,
    role: t("clients.partners.dyncorp.role"),
    description: t("clients.partners.dyncorp.description"),
  },
  {
    name: "IVY Japan",
    logo: ivyJapan,
    role: t("clients.partners.ivy.role"),
    description: t("clients.partners.ivy.description"),
  },
  {
    name: "KBR",
    logo: kbr,
    role: t("clients.partners.kbr.role"),
    description: t("clients.partners.kbr.description"),
  },
  {
    name: "MAG",
    logo: mag,
    role: t("clients.partners.mag.role"),
    description: t("clients.partners.mag.description"),
  },
  {
    name: "Samaritan's Purse",
    logo: samaritansPurse,
    role: t("clients.partners.samaritans.role"),
    description: t("clients.partners.samaritans.description"),
  },
  {
    name: "UNFPA",
    logo: unfpa,
    role: t("clients.partners.unfpa.role"),
    description: t("clients.partners.unfpa.description"),
  },
  {
    name: "UNHCR",
    logo: unhcr,
    role: t("clients.partners.unhcr.role"),
    description: t("clients.partners.unhcr.description"),
  },
  {
    name: "US Army",
    logo: usArmy,
    role: t("clients.partners.usarmy.role"),
    description: t("clients.partners.usarmy.description"),
  },
  {
    name: "Vectrus",
    logo: vectrus,
    role: t("clients.partners.vectrus.role"),
    description: t("clients.partners.vectrus.description"),
  },
  {
    name: "Yazda",
    logo: yazda,
    role: t("clients.partners.yazda.role"),
    description: t("clients.partners.yazda.description"),
  },
];

/**
 * DESKTOP_LOCATIONS
 * Americas y-values moved down so they sit under the hero text.
 */
const getDesktopLocations = (partners) => [
  { x: 28.6, y: 38, mobileX: 20, mobileY: 30, partner: partners[8] }, // US Army
  { x: 23.5, y: 40, mobileX: 15, mobileY: 35, partner: partners[3] }, // KBR
  { x: 29.4, y: 36, mobileX: 25, mobileY: 25, partner: partners[6] }, // UNFPA
  { x: 57, y: 36, mobileX: 60, mobileY: 35, partner: partners[10] }, // Yazda
  { x: 89.0, y: 28.7, mobileX: 85, mobileY: 25, partner: partners[2] }, // IVY Japan
  { x: 51.7, y: 24.3, mobileX: 55, mobileY: 20, partner: partners[7] }, // UNHCR
  { x: 28.5, y: 42, mobileX: 25, mobileY: 40, partner: partners[0] }, // Ammentun
  { x: 28.6, y: 39, mobileX: 30, mobileY: 32, partner: partners[1] }, // DynCorp
  { x: 49.4, y: 20.3, mobileX: 50, mobileY: 15, partner: partners[4] }, // MAG
  { x: 27.3, y: 44, mobileX: 20, mobileY: 45, partner: partners[5] }, // Samaritan's Purse
  { x: 20.9, y: 41, mobileX: 10, mobileY: 40, partner: partners[9] }, // Vectrus
];

const LOGOS_WITH_WHITE_BG = [
  "UNFPA",
  "Yazda",
  "IVY Japan",
  "DynCorp International",
];

const Clients = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar" || i18n.language === "ku";
  const [isMobile, setIsMobile] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [activePartner, setActivePartner] = useState(null);
  const [slideDirection, setSlideDirection] = useState(1);

  const PARTNERS = getPartners(t);
  const DESKTOP_LOCATIONS = getDesktopLocations(PARTNERS);
  const numPartners = DESKTOP_LOCATIONS.length;

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // 🔧 Always set activePartner based on activeIndex (desktop + mobile)
  useEffect(() => {
    if (numPartners > 0) {
      setActivePartner(DESKTOP_LOCATIONS[activeIndex].partner);
    }
  }, [activeIndex, numPartners, i18n.language]);

  const handleNext = () => {
    setSlideDirection(1);
    setActiveIndex((prevIndex) => (prevIndex + 1) % numPartners);
  };

  const handlePrev = () => {
    setSlideDirection(-1);
    setActiveIndex((prevIndex) => (prevIndex - 1 + numPartners) % numPartners);
  };

  const handleDotClick = (index) => {
    if (index > activeIndex) setSlideDirection(1);
    else if (index < activeIndex) setSlideDirection(-1);
    setActiveIndex(index);
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      transition: { duration: 0.4, ease: "easeOut" },
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 300 : -300,
      opacity: 0,
      transition: { duration: 0.4, ease: "easeIn" },
    }),
  };

  const activeLocation = DESKTOP_LOCATIONS[activeIndex];

  const mapLogoSizeClass = (name) => (name === "UNHCR" ? "h-8 w-8" : "h-9 w-9");

  const cardLogoSizeClass = (name) =>
    name === "UNHCR" ? "h-10 w-10" : "h-11 w-11";

  const circleBgClass = (name) =>
    LOGOS_WITH_WHITE_BG.includes(name) ? "bg-white" : "bg-slate-700/40";

  return (
    <section className="relative w-full h-[90vh] min-h-[650px] md:min-h-[700px] bg-[#020617] overflow-hidden selection:bg-blue-500/30 font-sans">
      {/* --- MAP BACKGROUND: Balanced & Premium --- */}
      <div className="absolute inset-0 z-0 select-none pointer-events-none">
        {/* 1. Map image */}
        <div className="absolute inset-0">
          <img
            src={worldMapImage}
            alt="World map"
            className="w-full h-full object-cover object-center opacity-40"
            style={{
              filter:
                "brightness(1.1) contrast(1.1) hue-rotate(200deg) saturate(1.2)",
            }}
          />
        </div>

        {/* 2. Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/80 via-transparent to-[#020617]/90" />

        {/* 3. Radial Vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_30%,#020617_95%)]" />

        {/* 4. Subtle Grid */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      {/* --- CONNECTING DATA ARCS (Desktop Only) - Removed to fix SVG path errors --- */}

      {/* --- MAP MARKERS --- */}
      <div className="absolute inset-0 z-20 h-full w-full">
        {activeLocation && (
          <motion.div
            key={activeLocation.partner.name}
            className="absolute group z-30 transform -translate-x-1/2 -translate-y-1/2"
            style={{
              top: isMobile
                ? `${activeLocation.mobileY}%`
                : `${activeLocation.y}%`,
              left: isMobile
                ? `${activeLocation.mobileX}%`
                : `${activeLocation.x}%`,
            }}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{
              opacity: 1,
              scale: 1,
              top: isMobile
                ? `${activeLocation.mobileY}%`
                : `${activeLocation.y}%`,
              left: isMobile
                ? `${activeLocation.mobileX}%`
                : `${activeLocation.x}%`,
            }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <div className="absolute inset-[-8px] rounded-full shadow-[0_0_25px_rgba(56,189,248,0.6)] opacity-60 animate-pulse-light" />
            <div
              className={
                "relative flex items-center justify-center overflow-hidden shadow-2xl rounded-full h-14 w-14 border border-white/20 backdrop-blur-sm " +
                circleBgClass(activeLocation.partner.name)
              }
            >
              <img
                src={activeLocation.partner.logo}
                alt={activeLocation.partner.name}
                className={
                  "object-contain rounded-full transition-all duration-300 " +
                  mapLogoSizeClass(activeLocation.partner.name)
                }
              />
            </div>
          </motion.div>
        )}
      </div>

      {/* --- TITLE & SLIDER --- */}
      <div className="relative z-30 container mx-auto h-full px-6 pointer-events-none">
        <div className="h-full flex flex-col justify-between py-10 md:py-12 lg:py-20">
          {/* Header */}
          <div className={`max-w-2xl pointer-events-auto ${isRTL ? '-mt-14' : ''}`}>
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-950/30 border border-cyan-500/20 backdrop-blur-md text-xs font-bold tracking-[0.2em] uppercase text-cyan-400 mb-4 md:mb-6 shadow-lg"
            >
              <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
              {t("clients.badge")}
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-3xl sm:text-4xl md:text-6xl font-medium text-white leading-[1.1] tracking-tight drop-shadow-2xl"
            >
              {t("clients.title")}
              <br className={isRTL ? 'block' : 'hidden'} />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-500 inline-block mt-5 pb-4">
                {t("clients.titleHighlight")}
              </span>
            </motion.h2>
          </div>

          {/* Slider Card */}
          <div className="pointer-events-auto self-start lg:mb-12 mt-6 md:mt-0">
            <div className="relative w-full max-w-md">
              <AnimatePresence
                initial={false}
                custom={slideDirection}
                mode="wait"
              >
                {activePartner && (
                  <motion.div
                    key={`${activePartner.name}-${i18n.language}`}
                    custom={slideDirection}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    className="w-full absolute md:static bg-slate-900/40 backdrop-blur-xl rounded-3xl border border-white/10 p-6 md:p-8 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.6)] relative overflow-hidden ring-1 ring-white/5"
                  >
                    <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-cyan-500/20 rounded-full blur-[60px] mix-blend-screen pointer-events-none" />

                    <div className="relative z-10 flex items-start gap-4 md:gap-6">
                      <div
                        className={
                          "flex-shrink-0 h-14 w-14 md:h-16 md:w-16 rounded-2xl flex items-center justify-center overflow-hidden shadow-lg border border-white/10 " +
                          circleBgClass(activePartner.name)
                        }
                      >
                        <img
                          src={activePartner.logo}
                          alt={activePartner.name}
                          className={
                            "object-contain opacity-95 " +
                            cardLogoSizeClass(activePartner.name)
                          }
                        />
                      </div>
                      <div>
                        <div className="text-lg md:text-xl font-bold text-white mb-1 tracking-wide">
                          {activePartner.name}
                        </div>
                        <p className="text-cyan-400 text-xs md:text-sm font-semibold uppercase tracking-wider mb-3">
                          {activePartner.role || "Global Partner"}
                        </p>
                        <div className="h-px w-full bg-gradient-to-r from-white/10 to-transparent mb-3 md:mb-4" />
                        <p className="text-slate-300 text-xs md:text-sm leading-relaxed font-light">
                          {activePartner.description}
                        </p>
                      </div>
                    </div>

                    <div className="absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-cyan-500/0 via-cyan-500/30 to-cyan-500/0" />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Slider controls – work on mobile & desktop */}
              <div className="absolute md:absolute top-1/2 transform -translate-y-1/2 w-full flex justify-between z-50 pointer-events-auto">
                <button
                  onClick={handlePrev}
                  aria-label="Previous Partner"
                  className="p-2.5 md:p-3.1 bg-white/10 backdrop-blur-md rounded-full text-white hover:bg-white/20 transition-colors shadow-lg -ml-4 md:-ml-5"
                >
                  <svg
                    className="w-4 h-4 md:w-5 md:h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d={isRTL ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"}
                    />
                  </svg>
                </button>
                <button
                  onClick={handleNext}
                  aria-label="Next Partner"
                  className="p-2.5 md:p-3 bg-white/10 backdrop-blur-md rounded-full text-white hover:bg-white/20 transition-colors shadow-lg -mr-4 md:-mr-5"
                >
                  <svg
                    className="w-4 h-4 md:w-5 md:h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d={isRTL ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"}
                    />
                  </svg>
                </button>
              </div>

              {/* Slider dots */}
              <div className="absolute -bottom-6 sm:-bottom-8 left-1/2 -translate-x-1/2 hidden md:flex gap-2 pointer-events-auto">
                {DESKTOP_LOCATIONS.map((_, index) => {
                  const isActive = activeIndex === index;
                  return (
                    <button
                      key={index}
                      onClick={() => handleDotClick(index)}
                      className={`
                        h-2 md:h-2.5 rounded-full transition-all duration-300
                        ${isActive
                          ? 'bg-cyan-400 w-4 md:w-5'
                          : 'bg-white/30 hover:bg-white/50 w-2 md:w-2.5'
                        }
                      `}
                      aria-label={`Go to partner ${index + 1}`}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse-light {
          0%, 100% { opacity: 0.8; transform: scale(1); } 
          50% { opacity: 0.4; transform: scale(1.05); } 
        }
        .animate-pulse-light {
          animation: pulse-light 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </section>
  );
};

export default Clients;
